import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  ArrowLeft,
  Loader2,
  Pencil,
  Trash2,
  Video,
  Wand2,
} from "lucide-react";
import {
  useUserStore,
  getDisplayUrl,
  type PlanType,
} from "@/stores/user-store";
import {
  useAssets,
  useCharacters,
  useDeleteAsset,
  useDeleteCharacter,
  useFavoriteAsset,
  useRenameCharacter,
} from "@/hooks";
import { mapAssetForDisplay, filterAssets } from "@/lib/asset-utils";
import { AssetGrid } from "@/components/features/assets/asset-grid";
import { VideoModal, ConfirmDialog, Toast } from "@/components/ui";
import { NameCharacterModal } from "./name-character-modal";
import { cn } from "@/lib/utils";
import type { Asset } from "@/types";

interface CharacterDetailProps {
  /** Character key (folder value). */
  name: string;
  onBack: () => void;
}

/**
 * Drill-in view for one character. Shows the currently-selected look
 * as a hero with Edit/Animate buttons that act on it; below, a strip
 * of every look (canonical "Base" first) lets the user swap which one
 * they're working with. Animations of the character live in their own
 * grid further down.
 */
export function CharacterDetail({ name, onBack }: CharacterDetailProps) {
  const navigate = useNavigate();
  const { plan } = useUserStore();
  const { data, isFetching } = useAssets({
    folder: name,
    bare: false,
    perPage: 200,
  });
  // Server tells us which row is the canonical (originally-saved
  // image) — falls back to local oldest-IMAGE detection if the
  // characters cache hasn't hydrated yet.
  const { data: charactersData } = useCharacters();
  const serverCanonicalId = useMemo(
    () =>
      charactersData?.characters.find((c) => c.name === name)?.canonical_id ??
      null,
    [charactersData, name],
  );
  const favoriteMutation = useFavoriteAsset();
  const deleteMutation = useDeleteAsset();
  const deleteCharacterMutation = useDeleteCharacter();
  const renameMutation = useRenameCharacter();
  const [playingVideo, setPlayingVideo] = useState<Asset | null>(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showDeleteError, setShowDeleteError] = useState(false);
  const [showRenameModal, setShowRenameModal] = useState(false);
  // Which look the hero + Edit/Animate buttons act on. Defaults to
  // the canonical and updates whenever the user clicks a thumbnail.
  const [selectedLookId, setSelectedLookId] = useState<string | null>(null);

  const handleRename = async (newName: string) => {
    const trimmed = newName.trim();
    if (!trimmed || trimmed === name) {
      setShowRenameModal(false);
      return;
    }
    try {
      await renameMutation.mutateAsync({ from: name, to: trimmed });
      setShowRenameModal(false);
      // The folder is the URL key, so swap routes after rename
      // succeeds — `useRenameCharacter`'s onSuccess already
      // invalidates the relevant queries.
      navigate(`/characters/${encodeURIComponent(trimmed)}`, { replace: true });
    } catch {
      // The mutation surfaces the error back to the modal via
      // `renameError` — keep the modal open so the user can retry
      // with a different name.
    }
  };

  const renameError = renameMutation.error
    ? (renameMutation.error as { errorMessage?: string; message?: string })
        .errorMessage ??
      (renameMutation.error as Error).message ??
      "Failed to rename character"
    : null;

  const handleDeleteCharacter = async () => {
    setShowDeleteConfirm(false);
    try {
      await deleteCharacterMutation.mutateAsync(name);
      // Bounce back to the library view; the character will already be
      // gone from the grid via React Query invalidation.
      navigate("/assets");
    } catch {
      setShowDeleteError(true);
    }
  };

  const assets = data?.assets ?? [];
  // Assets arrive newest-first. Canonical = the row the server flagged
  // as canonical (originally-saved image), or as a fallback the
  // oldest IMAGE locally. Everything else is a "look" — newest-first
  // so the most recent edits surface at the top of the strip.
  const { canonical, looks, animations } = useMemo(() => {
    const images: Asset[] = [];
    const videos: Asset[] = [];
    for (const a of assets) {
      if (a.media_type === "IMAGE") images.push(a);
      else if (a.media_type === "VIDEO") videos.push(a);
    }
    let canonical: Asset | null = null;
    if (serverCanonicalId) {
      canonical = images.find((a) => a.id === serverCanonicalId) ?? null;
    }
    if (!canonical) {
      // Fallback: oldest IMAGE in the folder. `images` is newest-first.
      canonical = images.length > 0 ? images[images.length - 1] : null;
    }
    const looks = canonical
      ? images.filter((a) => a.id !== canonical!.id)
      : [];
    return { canonical, looks, animations: videos };
  }, [assets, serverCanonicalId]);

  // Reset the user-selected look whenever the underlying canonical
  // changes (e.g., we just hydrated and learned the real one), so the
  // hero defaults back to the right base.
  useEffect(() => {
    setSelectedLookId(null);
  }, [canonical?.id]);

  const activeLook: Asset | null = useMemo(() => {
    if (!selectedLookId) return canonical;
    const found = looks.find((a) => a.id === selectedLookId);
    return found ?? canonical;
  }, [selectedLookId, canonical, looks]);

  // Edit + Animate both act on whichever look is currently selected
  // (defaults to the canonical/base). Edit routes into the dedicated
  // `/characters/edit` flow with `fromId=` so the new edit branches
  // off the right source. Animate hands the look's url/id straight
  // into `/video/i2v`, with the `folder` so the resulting video lands
  // in the same character.
  const handleEdit = () => {
    if (!activeLook) return;
    navigate(
      `/characters/edit?name=${encodeURIComponent(name)}&fromId=${activeLook.id}`,
    );
  };

  const handleAnimate = () => {
    if (!activeLook) return;
    navigate("/video/i2v", {
      state: {
        imageUrl: activeLook.url,
        imageId: activeLook.id,
        folder: name,
      },
    });
  };

  const headerProps = {
    name,
    onBack,
    onDelete: () => setShowDeleteConfirm(true),
    onRename: () => {
      // Clear any leftover error from a previous failed rename so
      // the modal doesn't flash stale text when reopened.
      renameMutation.reset();
      setShowRenameModal(true);
    },
    isDeleting: deleteCharacterMutation.isPending,
  };

  // While the canonical image is still loading we show a skeleton hero.
  if (isFetching && assets.length === 0) {
    return (
      <div className="space-y-6">
        <Header {...headerProps} />
        <div className="aspect-[3/4] sm:aspect-[16/9] rounded-2xl bg-white/[0.03] animate-pulse" />
      </div>
    );
  }

  if (!canonical) {
    return (
      <div className="space-y-6">
        <Header {...headerProps} />
        <div className="flex flex-col items-center justify-center py-20 text-center">
          <p className="text-sm text-[#888] max-w-sm">
            This character doesn't have any images yet. It might have been
            removed.
          </p>
        </div>
      </div>
    );
  }

  const animationCards = animations.map((a) => mapAssetForDisplay(a, plan));
  const filteredAnimations = filterAssets(animationCards, "video");

  return (
    <div className="space-y-6">
      <Header {...headerProps} />

      {/* Hero — shows the currently-selected look (canonical by
          default). Edit + Animate below act on whatever's shown. */}
      <div className="rounded-2xl overflow-hidden bg-black border border-white/[0.08]">
        <img
          src={getDisplayUrl(activeLook ?? canonical, plan)}
          alt={name}
          className="w-full max-h-[60vh] object-contain"
        />
      </div>

      {/* Primary actions — operate on the currently-selected look. */}
      <div className="flex flex-col sm:flex-row gap-3">
        <button
          onClick={handleEdit}
          className="flex-1 flex items-center justify-center gap-2 px-4 py-3 rounded-xl bg-white/[0.05] hover:bg-white/[0.1] transition-colors text-white border border-white/[0.08]"
        >
          <Wand2 className="size-5" />
          Edit
        </button>
        <button
          onClick={handleAnimate}
          className="flex-1 flex items-center justify-center gap-2 px-4 py-3 rounded-xl bg-gradient-to-r from-violet-500 to-purple-500 text-white hover:opacity-90 transition-opacity"
        >
          <Video className="size-5" />
          Animate
        </button>
      </div>

      {/* Looks — Base (canonical) + every edit. Click to swap which
          look the hero + Edit/Animate buttons act on. Always
          expanded; edits never silently replace the base. */}
      {(canonical || looks.length > 0) && (
        <div className="space-y-3">
          <h2 className="text-sm font-medium text-white/80">
            Looks
            <span className="ml-2 text-xs text-[#555]">
              {(canonical ? 1 : 0) + looks.length}
            </span>
          </h2>
          <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 gap-2">
            {canonical && (
              <LookChip
                asset={canonical}
                label="Base"
                isActive={activeLook?.id === canonical.id}
                onSelect={() => setSelectedLookId(null)}
                plan={plan}
              />
            )}
            {looks.map((l) => (
              <LookChip
                key={l.id}
                asset={l}
                isActive={activeLook?.id === l.id}
                onSelect={() => setSelectedLookId(l.id)}
                plan={plan}
              />
            ))}
          </div>
        </div>
      )}

      {/* Animations */}
      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <h2 className="text-sm font-medium text-white/80 flex items-center gap-2">
            <Video className="size-4 text-[#888]" />
            Animations
            <span className="text-xs text-[#555]">
              {animations.length}
            </span>
          </h2>
        </div>
        {animations.length === 0 ? (
          <div className="rounded-2xl bg-white/[0.02] border border-dashed border-white/[0.08] py-10 text-center">
            <p className="text-sm text-[#666]">
              No animations yet. Click Animate to generate one.
            </p>
          </div>
        ) : (
          <AssetGrid
            assets={filteredAnimations}
            onPlay={setPlayingVideo}
            onToggleFavorite={(id) => favoriteMutation.mutate(id)}
            onCopyPrompt={(prompt) => navigator.clipboard.writeText(prompt)}
            onDelete={(id) => deleteMutation.mutate(id)}
          />
        )}
      </div>

      {playingVideo && (
        <VideoModal
          isOpen={!!playingVideo}
          onClose={() => setPlayingVideo(null)}
          videoUrl={getDisplayUrl(playingVideo, plan)}
          title={playingVideo.prompt ?? "Animation"}
          dimensions={{
            width: playingVideo.width,
            height: playingVideo.height,
          }}
        />
      )}

      <ConfirmDialog
        isOpen={showDeleteConfirm}
        onClose={() => setShowDeleteConfirm(false)}
        onConfirm={handleDeleteCharacter}
        title={`Delete "${name}"?`}
        description="This deletes the character along with all its tweaks and animations. You can't undo this."
        confirmLabel="Delete Character"
        variant="danger"
      />

      <Toast
        isOpen={showDeleteError}
        onClose={() => setShowDeleteError(false)}
        type="error"
        message="Failed to delete character. Please try again."
        duration={3000}
      />

      <NameCharacterModal
        isOpen={showRenameModal}
        onClose={() => {
          // Block close while rename is in flight — prevents the user
          // from double-firing by closing-and-reopening between
          // request and the cache invalidation that swaps the route.
          if (renameMutation.isPending) return;
          setShowRenameModal(false);
        }}
        onSubmit={handleRename}
        title="Rename Character"
        submitLabel="Rename"
        defaultName={name}
        isSubmitting={renameMutation.isPending}
        error={renameError}
      />
    </div>
  );
}

interface LookChipProps {
  asset: Asset;
  /** Optional label overlay — used to mark the canonical as "Base". */
  label?: string;
  isActive: boolean;
  onSelect: () => void;
  plan: PlanType;
}

function LookChip({ asset, label, isActive, onSelect, plan }: LookChipProps) {
  return (
    <button
      type="button"
      onClick={onSelect}
      title={label ?? "Use this look"}
      className={cn(
        "group relative aspect-[3/4] rounded-lg overflow-hidden bg-black border transition-all",
        isActive
          ? "border-violet-400/80 ring-2 ring-violet-400/40"
          : "border-white/[0.06] hover:border-white/[0.2]",
      )}
    >
      <img
        src={getDisplayUrl(asset, plan)}
        alt={label ?? "Look"}
        loading="lazy"
        className="absolute inset-0 size-full object-cover group-hover:scale-[1.03] transition-transform"
      />
      {asset.status !== "completed" && (
        <div className="absolute inset-0 flex items-center justify-center bg-black/60">
          <Loader2 className="size-4 text-white animate-spin" />
        </div>
      )}
      {label && (
        <div className="absolute inset-x-0 bottom-0 px-2 py-1 bg-gradient-to-t from-black/80 to-transparent text-[10px] font-medium text-white">
          {label}
        </div>
      )}
    </button>
  );
}

interface HeaderProps {
  name: string;
  onBack: () => void;
  onDelete: () => void;
  onRename: () => void;
  isDeleting: boolean;
}

function Header({ name, onBack, onDelete, onRename, isDeleting }: HeaderProps) {
  return (
    <div className="flex items-center gap-3">
      <button
        onClick={onBack}
        className="flex items-center justify-center size-9 rounded-lg bg-white/[0.04] hover:bg-white/[0.08] text-[#aaa] hover:text-white transition-colors"
      >
        <ArrowLeft className="size-5" />
      </button>
      <button
        type="button"
        onClick={onRename}
        title="Rename character"
        className="group flex-1 min-w-0 flex items-center gap-2 text-left rounded-lg px-1 -mx-1 hover:bg-white/[0.04] transition-colors"
      >
        <h1 className="flex-1 min-w-0 text-lg sm:text-xl md:text-2xl font-bold text-white line-clamp-2">
          {name}
        </h1>
        <Pencil className="size-4 text-[#666] group-hover:text-white transition-colors shrink-0" />
      </button>
      <button
        type="button"
        onClick={onDelete}
        disabled={isDeleting}
        title="Delete character"
        className="flex items-center justify-center size-9 rounded-lg bg-white/[0.04] hover:bg-red-500/15 text-[#888] hover:text-red-300 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
      >
        {isDeleting ? (
          <Loader2 className="size-4 animate-spin" />
        ) : (
          <Trash2 className="size-4" />
        )}
      </button>
    </div>
  );
}
